import { useState } from "react";
import { motion } from "framer-motion";
import { HeartHandshake, Shield, Sun, HandHeart, Lock, Check } from "lucide-react";

const VALUES = [
  { key: "kindness", label: "Kindness", icon: HeartHandshake, color: "#FF6B6B", bg: "#FFE5E5" },
  { key: "courage", label: "Courage", icon: Shield, color: "#0284C7", bg: "#E0F2FE" },
  { key: "gratitude", label: "Gratitude", icon: Sun, color: "#FFC107", bg: "#FEF3C7" },
  { key: "forgiveness", label: "Forgiveness", icon: HandHeart, color: "#9333EA", bg: "#F3E8FF" },
];

const Grow = () => {
  const [unlocked, setUnlocked] = useState(["kindness"]);
  const toggle = (key) =>
    setUnlocked((u) => (u.includes(key) ? u.filter((k) => k !== key) : [...u, key]));

  return (
    <section id="grow" className="relative flex min-h-[100svh] flex-col justify-center bg-[#F8F5EC] py-28 sm:py-36" data-testid="grow-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF6B6B]"
            >
              Grow
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.08 }}
              className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl"
            >
              More than songs. Little lessons in big virtues.
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.16 }}
              className="mt-5 text-base text-slate-600 leading-relaxed sm:text-lg"
            >
              Every episode and activity gently practises a fruit of the Spirit — kindness at
              the playground, courage on the first day of school, gratitude at the dinner
              table, forgiveness after a squabble.
            </motion.p>
            <div className="mt-8 grid grid-cols-2 gap-3">
              {VALUES.map((v, i) => (
                <motion.div
                  key={v.key}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 + i * 0.07 }}
                  className="flex items-center gap-3 rounded-2xl bg-white p-4 shadow-sm"
                >
                  <span className="grid h-10 w-10 place-items-center rounded-xl" style={{ backgroundColor: v.bg }}>
                    <v.icon className="h-5 w-5" style={{ color: v.color }} />
                  </span>
                  <span className="font-display text-base font-semibold text-slate-800">{v.label}</span>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="rounded-3xl border border-[#E2DDD0] bg-white p-8 shadow-[0_20px_40px_-10px_rgba(14,165,233,0.15)] sm:p-10"
            data-testid="faith-badge-widget"
          >
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-slate-400">Sneak peek</p>
            <h3 className="mt-2 font-display text-2xl font-bold text-slate-900">Faith Badge Collector</h3>
            <p className="mt-2 text-sm text-slate-500">
              Kids unlock a badge when the family finishes a value challenge together. Tap to
              try it:
            </p>
            <div className="mt-7 grid grid-cols-2 gap-4">
              {VALUES.map((v) => {
                const isOn = unlocked.includes(v.key);
                return (
                  <motion.button
                    key={v.key}
                    onClick={() => toggle(v.key)}
                    whileTap={{ scale: 0.88 }}
                    data-testid={`badge-${v.key}`}
                    className={`relative flex flex-col items-center gap-2 rounded-2xl border-2 p-5 transition-colors ${
                      isOn ? "border-transparent" : "border-dashed border-slate-200 bg-[#F8F5EC]"
                    }`}
                    style={isOn ? { backgroundColor: v.bg } : {}}
                  >
                    <motion.span
                      animate={isOn ? { scale: [1, 1.3, 1], rotate: [0, -12, 0] } : {}}
                      transition={{ duration: 0.5 }}
                      className="grid h-12 w-12 place-items-center rounded-full"
                      style={{ backgroundColor: isOn ? v.color : "#E2E8F0" }}
                    >
                      {isOn ? (
                        <Check className="h-6 w-6 text-white" strokeWidth={3} />
                      ) : (
                        <Lock className="h-5 w-5 text-slate-400" />
                      )}
                    </motion.span>
                    <span
                      className={`font-display text-sm font-semibold ${isOn ? "text-slate-900" : "text-slate-400"}`}
                    >
                      {v.label}
                    </span>
                  </motion.button>
                );
              })}
            </div>
            <div className="mt-6 flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-500" data-testid="badge-progress">
                {unlocked.length} of {VALUES.length} badges unlocked
              </p>
              <div className="h-2 w-32 overflow-hidden rounded-full bg-[#F8F5EC]">
                <motion.div
                  className="h-full rounded-full bg-[#FFC107]"
                  animate={{ width: `${(unlocked.length / VALUES.length) * 100}%` }}
                  transition={{ type: "spring", stiffness: 120, damping: 20 }}
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Grow;
